import { onBeforeUnmount, watch } from 'vue'
import { useChatScope } from '@/composables/useChatScope'
import { useSavedPromptsStore } from '@/stores/savedPrompts'

/**
 * Own the saved-prompts subscription for the whole app (MYAC-1140).
 *
 * Called ONCE, from `HomeView` — the authenticated shell that outlives every
 * page. Saved prompts belong to the chat, and the chat is everywhere: the dock
 * is mounted above the keyed router-view so the conversation follows the member
 * from page to page (MYAC-917). A listener the picker depends on therefore has
 * to outlive any single view.
 *
 * It used to be owned by whoever happened to want it — `ChatInterface`, and
 * then the two saved-prompt pages, each subscribing on mount and the pages
 * tearing down on unmount. All three share ONE store, so a page's unmount
 * killed the listener the still-mounted dock was reading from, and nothing
 * re-subscribed: `subscribe` no-ops while the project is unchanged, and
 * `ChatInterface`'s watcher only fires on a project switch. Walking off
 * `…/chat/prompts` onto any other page of the same project left the picker
 * saying "Loading…" for the rest of the session.
 *
 * One owner, keyed on the project the CHAT is about (`useChatScope`, MYAC-1348):
 * the route's project on a project page, the conversation's or the picked one on
 * the dashboard, none while the chat asks which. `subscribe(pid)` no-ops on the
 * same pid and re-points on a new one, and `subscribe('')` is the teardown when
 * the chat has no project. The scope learns the route from `GlobalChatPanel`'s
 * immediate route watcher, mounted in the same shell, so a direct page load is
 * subscribed by the time the picker renders.
 *
 * The teardown on unmount is safe HERE and nowhere else: the shell unmounts
 * only on the way out of the authenticated app, when nothing is left to read
 * the prompts; without it the listener would outlive the session and fail its
 * next snapshot on permissions.
 */
export function useSavedPromptsSubscription(): void {
  const { scopeProjectId } = useChatScope()
  const savedPromptsStore = useSavedPromptsStore()

  watch(
    () => scopeProjectId.value,
    (pid) => savedPromptsStore.subscribe(pid || ''),
    { immediate: true },
  )

  onBeforeUnmount(() => savedPromptsStore.unsubscribe())
}
